import {useMemo} from 'react'

import {
  BuildSlotRequestResult,
  useSlotManagementContext,
  useSlotRequests
} from '../../../slotManagement'

import {useOrderIntakeSlotOrgUnitId} from './useOrderIntakeSlotOrgUnitId'

/**
 * Result of validation enablement check for Order Intake
 */
export interface OrderIntakeSlotValidationEnabledResult {
  /** Whether validation should run */
  isEnabled: boolean
  /** Slot request build results for all dateTimes (params, validity, missing fields) */
  slotRequestResults: BuildSlotRequestResult[]
}

/**
 * Hook to determine if Order Intake slot validation should be enabled.
 *
 * Validation is enabled when:
 * - Feature toggle is on
 * - At least one slot request can be built successfully
 *
 * This logic is shared between query-based and mutation-based validation.
 *
 * @returns Enablement status and slot request results
 */
export const useOrderIntakeSlotValidationEnabled = (): OrderIntakeSlotValidationEnabledResult => {
  const {config} = useSlotManagementContext()
  const orgUnitId = useOrderIntakeSlotOrgUnitId(config.orgUnitId)

  // Build slot request parameters for each dateTime
  const slotRequestResults = useSlotRequests({
    orgUnitId,
    siteNumber: config.siteNumber,
    normalizeDate: false // Validation endpoint needs exact time
  })

  const isEnabled = useMemo(
    () => config.isFeatureEnabled && slotRequestResults.some((r) => r.isValid),
    [config.isFeatureEnabled, slotRequestResults]
  )

  return {isEnabled, slotRequestResults}
}
